import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Activity, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Detail,
  DetailList,
  EmptyState,
  Page,
  PageBody,
  PageHeader,
  Panel,
  Stat,
  StatRow,
  Toolbar,
} from '@/ui/Panel';
import { Callout } from '@/ui/Callout';
import { DataTable, type Column } from '@/ui/DataTable';
import { SplitView } from '@/ui/SplitView';
import { ChipGroup } from '@/ui/Filters';
import { Button } from '@/ui/Button';
import { Badge, Code } from '@/ui/Badge';
import { ConfirmDialog, Dialog } from '@/ui/Dialog';
import { Field, Textarea } from '@/ui/Field';
import { ApiError, api, errorMessage } from '@/lib/api/client';
import { useAtLeast } from '@/lib/auth';
import { useShortcutHint } from '@/app/StatusLine';
import { dateTime, humanize, num, relativeTime, smartDate } from '@/lib/format';
import type { RegisterSummary } from '@/lib/api/types';

/**
 * Quarantined sync records — the financial dead-letter queue.
 *
 * When a register syncs up a sale, an account payment or a drawer movement the
 * server cannot apply, it is parked here rather than wedging the batch or being
 * dropped. Every row is money that has not landed. Two exits:
 *
 *   **Retry** replays the stored payload through the same money-path chokepoint
 *   the register uses, so it is idempotent — an already-applied record is
 *   acknowledged rather than double-applied. A 422 means the server refused:
 *   unsupported kind, no replay-safe dedup anchor, or it is simply still
 *   failing. That reason is shown verbatim, because it tells the operator what
 *   to fix.
 *
 *   **Discard** permanently abandons the record with a mandatory note. Nothing
 *   is applied; the money never posts. Typed confirmation.
 *
 * If the list itself fails to load, the failure is shown. An empty table must
 * never be read as "nothing is quarantined".
 */

interface SyncQuarantineRow {
  id: number;
  registerId: number | null;
  kind: string;
  clientRef: string | null;
  payloadFingerprint: string | null;
  payload: unknown;
  error: string;
  status: string;
  quarantinedAt: string;
  resolvedAt: string | null;
  resolvedBy: number | null;
  resolution: string | null;
}

/** Kinds the retry route can actually replay. Anything else is discard-only. */
const RETRYABLE_KINDS = ['transaction', 'customer_payment', 'cash_shift', 'cash_event'];

type KindFilter =
  | 'all'
  | 'transaction'
  | 'customer_payment'
  | 'cash_shift'
  | 'cash_event';

const KIND_OPTIONS: Array<{ value: KindFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'transaction', label: 'Sales' },
  { value: 'customer_payment', label: 'Account payments' },
  { value: 'cash_shift', label: 'Drawer shifts' },
  { value: 'cash_event', label: 'Drawer movements' },
];

interface SyncSearch {
  id?: number;
  kind: KindFilter;
}

export const Route = createFileRoute('/_app/system/sync')({
  staticData: { crumb: 'Sync health' },
  validateSearch: (s: Record<string, unknown>): SyncSearch => ({
    id: Number(s.id) > 0 ? Number(s.id) : undefined,
    kind: KIND_OPTIONS.some((o) => o.value === s.kind) ? (s.kind as KindFilter) : 'all',
  }),
  component: SyncScreen,
});

function SyncScreen() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  useShortcutHint([
    { keys: 'j / k', label: 'Move' },
    { keys: 'Enter', label: 'Inspect' },
  ]);

  function patch(next: Partial<SyncSearch>) {
    void navigate({ search: (prev: SyncSearch) => ({ ...prev, ...next }) });
  }

  const quarantine = useQuery({
    queryKey: ['sync-quarantine'],
    queryFn: () => api.get<SyncQuarantineRow[]>('/admin/sync-quarantine'),
    refetchInterval: 60_000,
  });

  const registers = useQuery({
    queryKey: ['registers'],
    queryFn: () => api.get<RegisterSummary[]>('/registers'),
    staleTime: 5 * 60_000,
  });

  const registerName = useMemo(() => {
    const map = new Map<number, string>();
    for (const r of registers.data ?? []) map.set(r.id, r.name);
    return map;
  }, [registers.data]);

  const all = quarantine.data;
  const rows = useMemo(
    () =>
      all === undefined
        ? undefined
        : all.filter((r) => (search.kind === 'all' ? true : r.kind === search.kind)),
    [all, search.kind]
  );

  const selected = rows?.find((r) => r.id === search.id);

  const totals = useMemo(() => {
    const list = all ?? [];
    return {
      total: list.length,
      sales: list.filter((r) => r.kind === 'transaction').length,
      resolving: list.filter((r) => r.status === 'resolving').length,
      unanchored: list.filter((r) => r.clientRef === null).length,
    };
  }, [all]);

  const columns: Array<Column<SyncQuarantineRow>> = [
    {
      id: 'quarantinedAt',
      header: 'Quarantined',
      width: '7rem',
      mono: true,
      cell: (row) => (
        <span title={dateTime(row.quarantinedAt)}>{smartDate(row.quarantinedAt)}</span>
      ),
    },
    {
      id: 'kind',
      header: 'Kind',
      width: '9rem',
      cell: (row) => <Badge tone="neutral">{humanize(row.kind)}</Badge>,
    },
    {
      id: 'status',
      header: 'State',
      width: '7rem',
      cell: (row) =>
        row.status === 'resolving' ? (
          <Badge tone="signal" dot pulse>
            Retrying
          </Badge>
        ) : (
          <Badge tone="negative" dot>
            Quarantined
          </Badge>
        ),
    },
    {
      id: 'register',
      header: 'Register',
      width: '8rem',
      hideBelow: 'lg',
      cell: (row) => (
        <span className="text-ink-muted truncate text-xs">
          {row.registerId === null
            ? '—'
            : (registerName.get(row.registerId) ?? `Register ${row.registerId}`)}
        </span>
      ),
    },
    {
      id: 'clientRef',
      header: 'Dedup anchor',
      width: '13rem',
      hideBelow: 'xl',
      cell: (row) =>
        row.clientRef ? (
          <Code copyable>{row.clientRef}</Code>
        ) : (
          <span className="text-2xs text-warn">none — cannot be replayed</span>
        ),
    },
    {
      id: 'error',
      header: 'Why it failed',
      cell: (row) => (
        <span className="text-ink-muted truncate text-xs" title={row.error}>
          {row.error}
        </span>
      ),
    },
  ];

  return (
    <Page>
      <PageHeader
        eyebrow="System"
        title="Sync health"
        subtitle="Financial records the server could not apply"
      />

      <Toolbar>
        <ChipGroup<KindFilter>
          value={search.kind}
          onChange={(v) => patch({ kind: v, id: undefined })}
          options={KIND_OPTIONS}
        />
        <Button
          size="sm"
          variant="ghost"
          className="ml-auto"
          icon={<RefreshCw className="size-3.5" />}
          loading={quarantine.isFetching}
          onClick={() => void quarantine.refetch()}
        >
          Refresh
        </Button>
      </Toolbar>

      <div className="border-line shrink-0 border-b px-4 py-3">
        <StatRow className="grid-cols-2 md:grid-cols-4 xl:grid-cols-4">
          <Stat
            label="Quarantined"
            value={quarantine.isError ? '—' : num(totals.total)}
            tone={totals.total > 0 ? 'negative' : undefined}
            sub={
              quarantine.isError ? 'Count unknown — list failed' : 'Money not yet applied'
            }
          />
          <Stat label="Sales" value={quarantine.isError ? '—' : num(totals.sales)} />
          <Stat
            label="Retry in flight"
            value={quarantine.isError ? '—' : num(totals.resolving)}
            tone={totals.resolving > 0 ? 'signal' : undefined}
          />
          <Stat
            label="No dedup anchor"
            value={quarantine.isError ? '—' : num(totals.unanchored)}
            tone={totals.unanchored > 0 ? 'warn' : undefined}
            sub="Discard-only"
          />
        </StatRow>
      </div>

      <PageBody bleed className="flex min-h-0 flex-col overflow-hidden">
        {quarantine.isError ? (
          <div className="shrink-0 px-4 pt-3">
            <Callout tone="negative" title="The quarantine list could not be loaded">
              {errorMessage(quarantine.error)} — this is <strong>not</strong> the same as
              an empty queue. Do not treat the table below as proof that nothing is
              stranded until this loads.
            </Callout>
          </div>
        ) : null}

        <div className="mt-0 min-h-0 flex-1">
          <SplitView
            storageKey="sync-quarantine"
            defaultDetail={480}
            maxDetail={780}
            onCloseDetail={() => patch({ id: undefined })}
            placeholder={
              <EmptyState
                compact
                icon={<Activity />}
                title="No record selected"
                description="Pick a row to read the payload the register sent and the error it hit."
              />
            }
            detail={
              selected ? (
                <QuarantineInspector key={selected.id} row={selected} />
              ) : undefined
            }
            list={
              <DataTable
                rows={rows}
                columns={columns}
                rowKey={(row) => row.id}
                loading={quarantine.isLoading}
                error={quarantine.error}
                onRetry={() => void quarantine.refetch()}
                onRowOpen={(row) => patch({ id: row.id })}
                isRowActive={(row) => row.id === search.id}
                rowTone={() => 'negative'}
                empty={{
                  icon: <Activity />,
                  title: 'Nothing quarantined',
                  description:
                    'Every record the registers sent has been applied. This list is live — it refreshes every minute.',
                }}
                footer={<span>{num(rows?.length ?? 0)} in this view</span>}
              />
            }
          />
        </div>
      </PageBody>
    </Page>
  );
}

/* ── Inspector ─────────────────────────────────────────────────────────────── */

function QuarantineInspector({ row }: { row: SyncQuarantineRow }) {
  const queryClient = useQueryClient();
  const isManager = useAtLeast('manager');
  const [retryOpen, setRetryOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const replayable = RETRYABLE_KINDS.includes(row.kind) && row.clientRef !== null;

  function refresh() {
    void queryClient.invalidateQueries({ queryKey: ['sync-quarantine'] });
    void queryClient.invalidateQueries({ queryKey: ['badges'] });
    void queryClient.invalidateQueries({ queryKey: ['transactions'] });
    void queryClient.invalidateQueries({ queryKey: ['cash'] });
  }

  const retry = useMutation({
    mutationFn: () =>
      api.post<{ id: number; status: string }>(`/admin/sync-quarantine/${row.id}/retry`),
    onSuccess: () => {
      toast.success('Record applied', {
        description: 'The payload replayed through the sync money path and cleared.',
      });
      setRefusal(null);
      setRetryOpen(false);
      refresh();
    },
    onError: (err) => {
      const status = err instanceof ApiError ? err.status : 0;
      if (status === 422) {
        // The server refusing to replay is the useful answer, not a crash:
        // unsupported kind, no dedup anchor, or the poison is still there.
        setRefusal(errorMessage(err));
        setRetryOpen(false);
      } else if (status === 409 || status === 404) {
        toast.info('Someone else already handled this', {
          description: `${errorMessage(err)} — refreshed with the current state.`,
        });
        setRetryOpen(false);
        refresh();
      } else {
        toast.error('The retry failed', { description: errorMessage(err) });
        setRetryOpen(false);
      }
    },
  });

  const payloadText = useMemo(() => {
    try {
      return JSON.stringify(row.payload, null, 2);
    } catch {
      return '// The stored payload could not be displayed.';
    }
  }, [row.payload]);

  return (
    <div className="space-y-3 p-3">
      <div>
        <div className="flex items-center gap-2">
          {row.status === 'resolving' ? (
            <Badge tone="signal" dot pulse>
              Retry in flight
            </Badge>
          ) : (
            <Badge tone="negative" dot>
              Quarantined
            </Badge>
          )}
          <Badge tone="neutral">{humanize(row.kind)}</Badge>
        </div>
        <div className="text-2xs text-ink-faint mt-1">
          Dead-lettered {dateTime(row.quarantinedAt)} · {relativeTime(row.quarantinedAt)}
        </div>
      </div>

      <Callout tone="negative" title="Why the server refused it">
        {row.error}
      </Callout>

      {refusal ? (
        <Callout tone="warn" title="The retry was refused">
          {refusal}
        </Callout>
      ) : null}

      {row.status === 'resolving' ? (
        <Callout tone="info" title="A retry claimed this record and never released it">
          It is still visible on purpose. Retrying again is safe — the replay deduplicates
          on the anchor below.
        </Callout>
      ) : null}

      {!replayable ? (
        <Callout tone="warn" title="This record cannot be replayed">
          {RETRYABLE_KINDS.includes(row.kind)
            ? 'It carries no replay-safe dedup anchor, so replaying it could apply the money twice. Reconcile it by hand and discard it with a note.'
            : `“${row.kind}” is not a kind the retry route can apply. Reconcile it by hand and discard it with a note.`}
        </Callout>
      ) : null}

      <Panel title="Record">
        <DetailList>
          <Detail label="Id" mono>
            {row.id}
          </Detail>
          <Detail label="Kind">{humanize(row.kind)}</Detail>
          <Detail label="Register" mono>
            {row.registerId ?? '—'}
          </Detail>
          <Detail label="Dedup anchor" mono>
            {row.clientRef ? <Code copyable>{row.clientRef}</Code> : 'none'}
          </Detail>
          {row.payloadFingerprint ? (
            <Detail label="Payload fingerprint" mono>
              <Code copyable>{row.payloadFingerprint.slice(0, 16)}</Code>
            </Detail>
          ) : null}
          {row.resolution ? <Detail label="Resolution">{row.resolution}</Detail> : null}
        </DetailList>
      </Panel>

      <Panel title="Payload as received" bleed>
        <pre className="scrollpane text-2xs text-ink-muted max-h-72 overflow-auto p-3 font-mono leading-relaxed">
          {payloadText}
        </pre>
      </Panel>

      {isManager ? (
        <div className="flex gap-2">
          {replayable ? (
            <Button
              variant="primary"
              size="sm"
              className="flex-1 justify-center"
              icon={<RefreshCw className="size-3.5" />}
              onClick={() => setRetryOpen(true)}
            >
              Retry
            </Button>
          ) : null}
          <Button
            variant="danger"
            size="sm"
            className="flex-1 justify-center"
            icon={<Trash2 className="size-3.5" />}
            onClick={() => setDiscardOpen(true)}
          >
            Discard
          </Button>
        </div>
      ) : (
        <Callout tone="info" title="Only a manager can clear these">
          Retrying replays money and discarding abandons it, so both need the
          `sync_quarantine.resolve` capability.
        </Callout>
      )}

      <ConfirmDialog
        open={retryOpen}
        onOpenChange={setRetryOpen}
        title="Replay this record"
        description="Runs the stored payload back through the same path the register uses."
        confirmLabel="Retry now"
        variant="primary"
        loading={retry.isPending}
        consequences={[
          `Replays this ${humanize(row.kind).toLowerCase()} through the sync money path — it may create a transaction, post to a house account, or move a drawer balance.`,
          'Idempotent: a record that already applied is acknowledged, not applied twice.',
          'If the underlying problem is still there it stays quarantined and tells you why.',
        ]}
        onConfirm={() => retry.mutate()}
      />

      <DiscardDialog
        open={discardOpen}
        onOpenChange={setDiscardOpen}
        row={row}
        onDone={refresh}
      />
    </div>
  );
}

/* ── Discard ──────────────────────────────────────────────────────────────── */

function DiscardDialog({
  open,
  onOpenChange,
  row,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: SyncQuarantineRow;
  onDone: () => void;
}) {
  const [stage, setStage] = useState<'form' | 'confirm'>('form');
  const [note, setNote] = useState('');

  const discard = useMutation({
    mutationFn: () =>
      api.post<{ id: number; status: string }>(
        `/admin/sync-quarantine/${row.id}/resolve`,
        { note: note.trim() }
      ),
    onSuccess: () => {
      toast.success('Record discarded', {
        description: 'Closed with your note in the audit log. Nothing was applied.',
      });
      close();
      onDone();
    },
    onError: (err) => {
      if (err instanceof ApiError && (err.status === 409 || err.status === 404)) {
        toast.info('Someone else already handled this', {
          description: `${errorMessage(err)} — refreshed with the current state.`,
        });
        close();
        onDone();
      } else {
        toast.error('Could not discard this record', {
          description: errorMessage(err),
        });
        setStage('form');
      }
    },
  });

  function close() {
    onOpenChange(false);
    setStage('form');
    setNote('');
  }

  const trimmed = note.trim();
  const noteOk = trimmed.length >= 1 && trimmed.length <= 1000;

  return (
    <>
      <Dialog
        open={open && stage === 'form'}
        onOpenChange={(next) => {
          if (!next) close();
        }}
        title="Discard this record"
        description="Abandons a financial record the register sent. The note is mandatory."
        width="sm"
        footer={
          <>
            <Button variant="ghost" onClick={close}>
              Cancel
            </Button>
            <Button
              variant="danger"
              disabled={!noteOk}
              onClick={() => setStage('confirm')}
            >
              Review consequences
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Callout tone="negative" title="This money will never post">
            Discarding does not apply the payload and never will. Use it only when the
            record has been reconciled by hand, or is genuinely unrecoverable.
          </Callout>
          <Field
            label="Note"
            required
            hint={`${trimmed.length}/1000 · written to the audit log against your account`}
            error={trimmed.length > 1000 ? 'Too long — 1000 characters maximum.' : null}
          >
            <Textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.currentTarget.value)}
              placeholder="Keyed into the ledger by hand on 14 Aug; register payload is a duplicate of txn 3f9c…"
            />
          </Field>
        </div>
      </Dialog>

      <ConfirmDialog
        open={open && stage === 'confirm'}
        onOpenChange={(next) => {
          if (!next) setStage('form');
        }}
        title="Permanently discard"
        description="There is no way back from this — the payload is closed, not applied."
        confirmLabel="Discard record"
        typeToConfirm="DISCARD"
        consequences={[
          `Permanently abandons this ${humanize(row.kind).toLowerCase()} from register ${row.registerId ?? '—'}.`,
          'The money it represents will never post. If it was real, the books stay wrong unless you have already reconciled it.',
          'Your note and identity are written to the audit log.',
          'Retry becomes impossible afterwards.',
        ]}
        loading={discard.isPending}
        onConfirm={() => discard.mutate()}
      />
    </>
  );
}
